import { useCallback, useEffect, useRef, useState } from 'react';
import { useStore } from '../store/store';
import { API_ENDPOINTS } from '../api/endpoints';
import { HEADER_MESSAGE_TYPES } from '../utils/constants/header';
import useEnrichmentAccess from './useEnrichmentAccess';

const FIELD_EMAIL = 'email';
const FIELD_PHONE = 'phone';

const POLL_INTERVAL_MS = 2_000;
const POLL_MAX_ATTEMPTS = 30; // 30 × 2s = 60s ceiling per spec discussion.

const ERROR_COPY = {
  // 410 is permanent erasure (GDPR DSR) — communicate finality so users
  // don't retry indefinitely. Wording from Phase 3a ux-critic feedback.
  410: 'Contact data has been removed and is no longer available.',
  502: 'Enrichment temporarily unavailable - try again later.',
  PENDING_TIMEOUT: 'Enrichment is taking longer than expected. Please try again.',
  UNKNOWN: 'Could not reveal contact data. Please try again.'
};

const idleState = () => ({ status: 'idle', value: null });

/**
 * Build the reveal POST body. A discovered contact's FIRST reveal (persist-on-
 * reveal) sends the identity to mint the row — including the discovery-time
 * seniority / job title / country (TD-2667) the BE stores via
 * `discoveredContactAttributes`. Once a `contact_enrichment_id` exists, the
 * regular endpoint just needs the company + person keys. Optional fields are
 * omitted when absent rather than sent as null.
 */
const buildRevealBody = ({
  firstDiscoveredReveal,
  field,
  profileId,
  idFirst,
  idLast,
  idLinkedin,
  idSeniority,
  idJobTitle,
  idCountry,
  companyId,
  personCrefoId
}) => {
  if (firstDiscoveredReveal) {
    return {
      profileId,
      contact: {
        first_name: idFirst,
        last_name: idLast,
        ...(idLinkedin ? { linkedin_url: idLinkedin } : {}),
        ...(idSeniority ? { seniority: idSeniority } : {}),
        ...(idJobTitle ? { job_title: idJobTitle } : {}),
        ...(idCountry ? { country: idCountry } : {})
      },
      field
    };
  }
  return {
    field,
    ...(companyId !== undefined ? { companyId } : {}),
    ...(personCrefoId !== undefined ? { personCrefoId } : {})
  };
};

/**
 * Per-contact reveal state machine for `personal_email` and `mobile_phone`.
 *
 * Response handling (HTTP status comes from the `httpStatus` field that
 * api.js injects on the response envelope; store.makeRequest forwards it
 * as the 2nd arg to onSuccess/onError):
 *
 *   - 200 + body.status === 'success'    → status='found', value=email|phone
 *   - 200 + body.status === 'not_found'  → status='not_found'
 *   - 202 + body.status === 'pending'    → re-POST after 2s, up to 30 attempts.
 *                                          With store.js's 2xx-as-success
 *                                          routing, 202 arrives in onSuccess.
 *   - 402                                → insufficientCredits flag flips on;
 *                                          both buttons should disable upstream.
 *   - 404                                → status='not_found' (BE may emit
 *                                          either 200+not_found body OR a
 *                                          bare 404 depending on cache state).
 *   - 410                                → toast (permanent erasure)
 *   - 502                                → toast (provider failure)
 *   - other                              → toast (generic)
 *
 * State is local to the hook instance — when the consumer remounts (e.g.,
 * KeyContacts list re-renders after a re-fetch), revealed values present on
 * the contact prop take precedence and the hook state resets to idle.
 *
 * @param {string|number} contactId  contact_enrichment.id
 * @param {object} [ctx]  reveal context. `companyId` is the profile id of the
 *   company in context — the BE derives the company name from it (Surfe rejects
 *   a person without a company). `personCrefoId` scopes the reveal to the
 *   specific person. Sourced from the company being viewed (Key Contacts tab)
 *   or the person's company relation (Contact Detail page). `enabled` (default
 *   true) gates the internal useEnrichmentAccess fetch: when the caller already
 *   owns a hoisted access instance (ContactCard with an `access` prop), it
 *   passes `enabled: false` so this hook does NOT also mount a
 *   /subscription-status fetch (TD-2263 — that ungated indirect call is what
 *   kept the N-card fan-out and the path-global cancelRequest race alive). The
 *   402 path still uses `refetchAccess`, which stays available even when the
 *   fetch is suppressed. `discovered` (Stream B): when true and no
 *   contact_enrichment_id exists yet, the first reveal is routed to
 *   /enrichment/discover-reveal with `profileId` (the company id) + `identity`
 *   ({ first_name, last_name, linkedin_url?, seniority?, job_title?, country? } —
 *   TD-2667: the optional attributes are persisted at mint so the Role/residency
 *   survive enrichment); the BE persists the contact and returns its new id,
 *   which is captured for the other field + any re-poll.
 *   `onRevealSuccess` (TD-2455): optional callback fired when a reveal resolves
 *   to a server-confirmed `success` (the point a credit is deducted BE-side).
 *   Lets a parent that owns the visible credit counter refresh it in place — the
 *   counter reads a SEPARATE useSubscriptionStatus instance, so the hook's own
 *   `refetchAccess` (402 path) can't move it; the parent must refetch its own
 *   instance. Pass a stable (memoised) reference so revealEmail/revealPhone stay
 *   referentially stable.
 * @returns {{
 *   emailState: { status: 'idle'|'loading'|'found'|'not_found', value: string|null },
 *   phoneState: { status: 'idle'|'loading'|'found'|'not_found', value: string|null },
 *   revealEmail: Function,
 *   revealPhone: Function,
 *   insufficientCredits: boolean
 * }}
 */
const useContactReveal = (
  contactId,
  {
    companyId,
    personCrefoId,
    enabled = true,
    discovered = false,
    profileId,
    identity,
    onRevealSuccess
  } = {}
) => {
  // Identity for a discovered contact's FIRST reveal (persist-on-reveal). Pulled
  // apart into primitives so the reveal callback stays referentially stable when
  // the caller passes a fresh `identity` object literal each render.
  const idFirst = identity?.first_name ?? null;
  const idLast = identity?.last_name ?? null;
  const idLinkedin = identity?.linkedin_url ?? null;
  // TD-2667: the seniority / job title / country the discovery search already
  // surfaced. Sent with the mint (discover-reveal) so the BE persists them at
  // mint time (parity with the job title) rather than leaving the Role to the
  // enrich webhook, which commonly omits the seniority enum → blank Role.
  const idSeniority = identity?.seniority ?? null;
  const idJobTitle = identity?.job_title ?? null;
  const idCountry = identity?.country ?? null;
  const store = useStore();
  // TD-2100 AC (verbatim): "On 402 ... subscriptionStatus.refetch() is
  // triggered so the chip flips to grey too." Pulled in here so the 402
  // branch can refresh the credit balance without the caller wiring it.
  // `enabled` is threaded through so a card consuming a hoisted access prop
  // suppresses this fetch too (TD-2263) — see the ctx docs above.
  const { refetch: refetchAccess } = useEnrichmentAccess({ enabled });
  const [emailState, setEmailState] = useState(idleState);
  const [phoneState, setPhoneState] = useState(idleState);
  const [insufficientCredits, setInsufficientCredits] = useState(false);
  const isMounted = useRef(true);
  const pollTimers = useRef({});
  // A discovered contact's first reveal persists it BE-side and returns a fresh
  // contact_enrichment_id; capture it so the other field + any re-poll use the
  // regular /enrichment/contact/{id}/reveal path. Seeded with contactId for
  // stored (and already-persisted discovered) contacts, which already have one.
  const resolvedCeIdRef = useRef(contactId ?? null);

  useEffect(() => {
    // Re-arm on mount so a remount (Strict Mode double-invoke, list-key reuse)
    // doesn't leave the ref stuck at false from a previous unmount.
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      Object.values(pollTimers.current).forEach((t) => clearTimeout(t));
      pollTimers.current = {};
    };
  }, []);

  const toast = useCallback(
    (message) => store.utilsStore?.setHeaderMessage?.(message, HEADER_MESSAGE_TYPES.ERROR),
    [store]
  );

  const setFieldState = useCallback((field, next) => {
    if (!isMounted.current) return;
    if (field === FIELD_EMAIL) setEmailState(next);
    else setPhoneState(next);
  }, []);

  const schedulePoll = useCallback(
    (field, attempt) => {
      if (attempt + 1 >= POLL_MAX_ATTEMPTS) {
        setFieldState(field, idleState());
        toast(ERROR_COPY.PENDING_TIMEOUT);
        return;
      }
      const key = `${field}:${attempt}`;
      pollTimers.current[key] = setTimeout(() => {
        delete pollTimers.current[key];
        // eslint-disable-next-line no-use-before-define
        issueReveal(field, attempt + 1);
      }, POLL_INTERVAL_MS);
    },
    [setFieldState, toast]
  );

  // Dispatch a successful reveal response. A discovered first-reveal returns the
  // freshly-minted contact_enrichment_id (present even on a 202 pending) — capture
  // it so the other field + any re-poll use the regular reveal endpoint.
  const handleRevealSuccess = useCallback(
    (field, attempt, data) => {
      if (data?.contact_enrichment_id && !resolvedCeIdRef.current) {
        resolvedCeIdRef.current = data.contact_enrichment_id;
      }
      // data.status is the BE's semantic status ('success'|'not_found'|'pending').
      if (data?.status === 'success') {
        setFieldState(field, { status: 'found', value: data.value ?? null });
        // TD-2455: a success deducts a credit BE-side; let the caller refresh the
        // visible credit counter (a separate subscription instance).
        onRevealSuccess?.();
      } else if (data?.status === 'not_found') {
        setFieldState(field, { status: 'not_found', value: null });
      } else {
        // 202 pending OR any unrecognised success shape — re-poll defensively.
        schedulePoll(field, attempt);
      }
    },
    [setFieldState, onRevealSuccess, schedulePoll]
  );

  // Map a reveal error response (HTTP status on `response.httpStatus`, api.js
  // passthrough) to inline state / toast.
  const handleRevealError = useCallback(
    (field, response) => {
      const statusCode = response?.httpStatus;
      if (statusCode === 402) {
        setInsufficientCredits(true);
        setFieldState(field, idleState());
        // TD-2100: refresh the credit balance so the badge/chip reflects zero.
        refetchAccess?.();
        return;
      }
      if (statusCode === 404) {
        // BE may serve 404 instead of 200+not_found when the row is missing
        // entirely (vs. cached not-found) — same user-facing outcome.
        setFieldState(field, { status: 'not_found', value: null });
        return;
      }
      if (statusCode === 410) {
        setFieldState(field, idleState());
        toast(ERROR_COPY[410]);
        return;
      }
      if (statusCode === 502) {
        setFieldState(field, idleState());
        toast(ERROR_COPY[502]);
        return;
      }
      setFieldState(field, idleState());
      toast(ERROR_COPY.UNKNOWN);
    },
    [setFieldState, refetchAccess, toast]
  );

  const issueReveal = useCallback(
    (field, attempt) => {
      if (!isMounted.current) return;

      const ceId = resolvedCeIdRef.current;
      // A discovered contact with no contact_enrichment_id yet: the first reveal
      // goes to /enrichment/discover-reveal, which persists it (mints disc_<uuid>)
      // and reveals in one call. Once we have the id (stored contact, or a
      // discovered one after its first reveal), use the regular reveal endpoint.
      const firstDiscoveredReveal = discovered && !ceId;

      if (firstDiscoveredReveal) {
        // discover-reveal requires first + last name (BE 400s otherwise); the
        // card disables the buttons when these are missing, but guard anyway.
        if (!profileId || !idFirst || !idLast) return;
      } else if (!ceId) {
        return;
      }

      setFieldState(field, { status: 'loading', value: null });

      const endpoint = firstDiscoveredReveal
        ? API_ENDPOINTS.ENRICHMENT_DISCOVER_REVEAL
        : {
            ...API_ENDPOINTS.ENRICHMENT_REVEAL_CONTACT,
            path: `/enrichment/contact/${ceId}/reveal`
          };

      const body = buildRevealBody({
        firstDiscoveredReveal,
        field,
        profileId,
        idFirst,
        idLast,
        idLinkedin,
        idSeniority,
        idJobTitle,
        idCountry,
        companyId,
        personCrefoId
      });

      store.makeRequest({
        endpoint,
        body,
        onSuccess: (data) => handleRevealSuccess(field, attempt, data),
        onError: (_msg, response) => handleRevealError(field, response)
      });
    },
    [
      discovered,
      profileId,
      idFirst,
      idLast,
      idLinkedin,
      idSeniority,
      idJobTitle,
      idCountry,
      companyId,
      personCrefoId,
      store,
      setFieldState,
      handleRevealSuccess,
      handleRevealError
    ]
  );

  const revealEmail = useCallback(() => issueReveal(FIELD_EMAIL, 0), [issueReveal]);
  const revealPhone = useCallback(() => issueReveal(FIELD_PHONE, 0), [issueReveal]);

  return { emailState, phoneState, revealEmail, revealPhone, insufficientCredits };
};

export default useContactReveal;
